import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import {
  ShieldCheck,
  GraduationCap,
  Microscope,
  Brain,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowUpRight,
  FileText,
  Lock,
  Activity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ELIGIBLE_ROLES = [
  {
    icon: Brain,
    title: 'Clinical Psychologists',
    note: 'M.Phil / Ph.D. in Clinical Psychology with active license (RCI / equivalent)'
  },
  {
    icon: GraduationCap,
    title: 'Cognitive & Educational Psychologists',
    note: 'Ph.D. or registered practitioner in cognitive, educational, or developmental psychology'
  },
  {
    icon: Microscope,
    title: 'Neuroscientists',
    note: 'Ph.D. in neuroscience, cognitive neuroscience, or affiliated research institution'
  },
  {
    icon: Users,
    title: 'Behavioral Scientists',
    note: 'Doctoral credential in behavioral science, behavior analysis (BCBA), or applied behavioral research'
  }
];

const NOT_ALLOWED = [
  'Pre-recorded video lectures or webinars',
  'Online "classes" or live tutorials',
  'PDF e-books, slide decks, or written-only content',
  'Affiliate links or third-party course funnels',
  'Listening-only podcasts or audio courses',
  'Aggregated content from public textbooks without original methodology',
  'Religious, political, astrological, or pseudoscientific material',
  'Programs not segmented into structured 1-hour sessions'
];

const REQUIREMENTS = [
  { label: 'Format', value: 'Activity-based · engagement-driven · interactive blocks only' },
  { label: 'Session length', value: 'Strict 1-hours (1 hour) per session — non-negotiable' },
  { label: 'Structure', value: 'Each session split into measurable blocks (recall, drill, decision, reflection, application)' },
  { label: 'Outcome', value: 'Every session must produce a quantifiable performance signal that updates the BrainMap Score' },
  { label: 'Originality', value: 'Methodology must be your own clinical / research IP — no paraphrased third-party material' },
  { label: 'Ethics', value: 'COPPA / DPDP compliant for minors · evidence-informed · no medical claims' }
];

export default function Marketplace() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/marketplace`)
      .then(res => setPrograms(res.data))
      .catch(() => setPrograms([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="marketplace-page">

      {/* HERO — Editorial / Elite */}
      <section className="border-b border-[#09090B] bg-[#09090B] text-white" data-testid="marketplace-hero">
        <div className="container-swiss py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <p className="mono-data text-zinc-400 mb-6" data-testid="mp-eyebrow">By Invitation · Practitioner Marketplace</p>
              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="mp-headline"
              >
                A marketplace built<br />for the people who<br />
                <span className="italic font-light">study the mind</span><br />
                — not sell to it.
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-zinc-300 text-base leading-relaxed border-l border-zinc-700 pl-6 max-w-md" data-testid="mp-deck">
                FOKYES is a closed marketplace for clinically-credentialed cognitive trainers. Every program here is an <strong className="text-white">activity-based, hour-long protocol</strong> — never a recorded class, never a video, never a watch-only experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS THIS — long-form explainer */}
      <section className="border-b border-[#09090B]" data-testid="mp-what-is">
        <div className="container-swiss py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="mono-data text-zinc-500 mb-3">01 / What This Is</p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Not a course platform. Not a class library.
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-zinc-700 leading-relaxed">
              <p className="text-lg">
                The Marketplace is the <strong>only</strong> place outside our in-house catalog where outside experts can publish cognitive training on FOKYES. It exists because real cognitive science is hard to find online — and almost impossible to do in front of a camera.
              </p>
              <p>
                Every program lives inside our session player as a sequence of measurable activities. Memory drills, decision games, attention sprints, behavioral rehearsals, structured reflection — interactive blocks that score the user, adapt in real time, and feed the BrainMap Score (0–900). The user does the work. They don't watch someone else do it.
              </p>
              <p>
                That is why this is not Udemy. It is not Coursera. It is not YouTube with a paywall. It is a clinical-grade training surface where credentialed practitioners codify their own methodology into hour-long, engagement-based protocols and earn from outcomes — not views.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHO CAN SELL */}
      <section className="border-b border-[#09090B] bg-zinc-50" data-testid="mp-eligibility">
        <div className="container-swiss py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-4">
              <p className="mono-data text-zinc-500 mb-3">02 / Who Can Sell</p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Credentials are the only currency.
              </h2>
            </div>
            <div className="lg:col-span-8 text-zinc-700 leading-relaxed">
              <p className="text-lg">
                Authorship is restricted to four categories of professionals. We verify every applicant's credentials before any program goes live. Influencers, content creators, life coaches, motivational speakers, and self-taught practitioners — however popular — are not eligible.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-[#09090B]" data-testid="eligible-grid">
            {ELIGIBLE_ROLES.map((role) => {
              const Icon = role.icon;
              return (
                <div key={role.title} className="border-r border-b border-[#09090B] p-8 bg-white" data-testid={`eligible-${role.title.split(' ')[0].toLowerCase()}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 border border-[#09090B] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {role.title}
                    </h3>
                  </div>
                  <p className="text-sm text-zinc-600 leading-relaxed">{role.note}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex items-start gap-3 max-w-3xl">
            <Lock className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-zinc-500 leading-relaxed">
              Credentials are verified against the Indian Rehabilitation Council, ABA certification boards, doctoral institutions, and peer publications where applicable. False claims result in permanent removal and forfeit of unpaid revenue.
            </p>
          </div>
        </div>
      </section>

      {/* FORMAT REQUIREMENTS */}
      <section className="border-b border-[#09090B]" data-testid="mp-format">
        <div className="container-swiss py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-4">
              <p className="mono-data text-zinc-500 mb-3">03 / Format Rules</p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
                One hour. One protocol. Zero passive content.
              </h2>
            </div>
            <div className="lg:col-span-8 text-zinc-700 leading-relaxed text-lg">
              <p>
                If your IP cannot be expressed as activity-driven hour-long sessions, this is not the platform for it. We do not negotiate on format. The 1-hour activity protocol is the entire product.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-[#09090B]">
            {/* Required */}
            <div className="p-8 lg:border-r border-b lg:border-b-0 border-[#09090B]" data-testid="format-required">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle2 className="w-5 h-5" />
                <h3 className="mono-data text-sm tracking-widest">Required of every program</h3>
              </div>
              <dl className="space-y-5">
                {REQUIREMENTS.map((r) => (
                  <div key={r.label} className="border-b border-zinc-200 pb-4 last:border-0 last:pb-0">
                    <dt className="mono-data text-[11px] text-zinc-500 mb-1">{r.label}</dt>
                    <dd className="text-sm text-zinc-800 leading-relaxed">{r.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Not allowed */}
            <div className="p-8 bg-[#09090B] text-white" data-testid="format-prohibited">
              <div className="flex items-center gap-3 mb-6">
                <XCircle className="w-5 h-5" />
                <h3 className="mono-data text-sm tracking-widest text-zinc-400">Will be rejected on submission</h3>
              </div>
              <ul className="space-y-3">
                {NOT_ALLOWED.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-300 leading-relaxed">
                    <span className="mono-data text-zinc-500 text-[10px] mt-1 flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / FLOW */}
      <section className="border-b border-[#09090B] bg-zinc-50" data-testid="mp-flow">
        <div className="container-swiss py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-4">
              <p className="mono-data text-zinc-500 mb-3">04 / Application & Build</p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
                From credentials to live program.
              </h2>
            </div>
            <div className="lg:col-span-8 text-zinc-700 leading-relaxed">
              <p>Our review team works with each applicant through five gates. There is no shortcut.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-0 border-l border-t border-[#09090B]" data-testid="mp-steps">
            {[
              { n: '01', t: 'Credential verification', d: 'Submit license, doctoral proof, and a 1-page methodology summary.' },
              { n: '02', t: 'Methodology review', d: 'Our review board (in-house clinicians + scientific advisors) evaluates rigor and originality.' },
              { n: '03', t: 'Session design', d: 'You map your protocol into 1-hour activity sessions using our authoring tools.' },
              { n: '04', t: 'Pilot cohort', d: 'A 30-user invite-only pilot validates engagement, completion, and outcome lift.' },
              { n: '05', t: 'Listing & revenue', d: 'Approved programs go live in the marketplace. 70% creator share, paid monthly.' }
            ].map(s => (
              <div key={s.n} className="border-r border-b border-[#09090B] p-6 bg-white">
                <p className="mono-data text-xs text-zinc-400 mb-3">{s.n}</p>
                <h4 className="font-bold text-base mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.t}</h4>
                <p className="text-xs text-zinc-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRENT LISTINGS or EMPTY */}
      <section className="border-b border-[#09090B]" data-testid="mp-listings">
        <div className="container-swiss py-20 md:py-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="mono-data text-zinc-500 mb-3">05 / Current Listings</p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="marketplace-heading">
                Approved practitioner programs
              </h2>
            </div>
            {user && (
              <Link to="/creator" className="btn-primary inline-flex items-center gap-2" data-testid="creator-portal-btn">
                <FileText className="w-4 h-4" /> Creator Portal
              </Link>
            )}
          </div>

          {loading ? (
            <div className="py-24 text-center" data-testid="marketplace-loading">
              <div className="mono-data text-sm animate-pulse-red">Loading approved programs…</div>
            </div>
          ) : programs.length === 0 ? (
            <div className="border border-[#09090B] p-12 md:p-16 text-center max-w-3xl mx-auto" data-testid="marketplace-empty">
              <Activity className="w-10 h-10 text-[#09090B] mx-auto mb-6" />
              <h3 className="text-2xl md:text-3xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                The marketplace opens with our first verified cohort.
              </h3>
              <p className="text-zinc-600 leading-relaxed max-w-xl mx-auto mb-8">
                We are currently onboarding our founding panel of clinical psychologists, cognitive neuroscientists, and behavioral scientists. Listings will appear here as their hour-long protocols are pilot-validated.
              </p>
              <a
                href="mailto:practitioners@fokyes.com?subject=Marketplace%20Application%20%E2%80%94%20Practitioner&body=Name%3A%0AInstitution%2FClinic%3A%0ACredential%20%2B%20License%20number%3A%0ATraining%20methodology%20(1-paragraph)%3A%0ASample%20outcome%20metric%3A%0A"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="apply-practitioner-btn"
              >
                Apply as a Practitioner <ArrowUpRight className="w-4 h-4" />
              </a>
              <p className="mono-data text-[11px] text-zinc-400 mt-6">
                practitioners@fokyes.com · Reviewed within 10 working days
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-[#09090B]" data-testid="mp-grid">
              {programs.map(p => (
                <Link
                  key={p.program_id}
                  to={`/programs/${p.program_id}`}
                  className="border-r border-b border-[#09090B] p-6 group bg-white hover:bg-zinc-50 transition-colors"
                  data-testid={`mp-card-${p.program_id}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="mono-data text-[11px] text-zinc-500">{p.category}</span>
                    <span className="text-zinc-300">·</span>
                    <span className="mono-data text-[11px] text-zinc-500">{p.session_count} × 3600s</span>
                  </div>
                  <h3 className="text-lg font-bold tracking-tight mb-2 group-hover:underline underline-offset-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-600 line-clamp-2 mb-6 leading-relaxed">{p.description}</p>
                  <div className="flex items-end justify-between pt-4 border-t border-zinc-200">
                    <div>
                      <p className="mono-data text-[10px] text-zinc-400 mb-0.5">By</p>
                      <p className="text-xs font-semibold text-zinc-800">{p.creator_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="mono-data text-[10px] text-zinc-400 mb-0.5">Price</p>
                      <p className="text-base font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹{p.price}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FOR USERS — trust footer */}
      <section className="bg-[#09090B] text-white" data-testid="mp-trust">
        <div className="container-swiss py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            <div>
              <ShieldCheck className="w-6 h-6 mb-4" />
              <h4 className="font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Verified credentials</h4>
              <p className="text-sm text-zinc-400 leading-relaxed">Every author is licensed or doctorally credentialed. We publish their qualifications on every program detail page.</p>
            </div>
            <div>
              <Clock className="w-6 h-6 mb-4" />
              <h4 className="font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Hour-long protocols only</h4>
              <p className="text-sm text-zinc-400 leading-relaxed">No videos. No tutorials. Each session is exactly 1-hours of structured cognitive activity that scores you in real time.</p>
            </div>
            <div>
              <Activity className="w-6 h-6 mb-4" />
              <h4 className="font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Outcome-graded</h4>
              <p className="text-sm text-zinc-400 leading-relaxed">Every protocol updates your BrainMap Score (0–900). If a program doesn't measurably improve outcomes, we delist it.</p>
            </div>
          </div>
          <p className="mono-data text-[11px] text-zinc-500 mt-12 max-w-3xl">
            FOKYES does not host pre-recorded video, classes, tutorials, or any passive media. The platform is an interactive cognitive training surface — by design, by policy, and by audit.
          </p>
        </div>
      </section>
    </div>
  );
}
